import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { CLIENT_TYPE_LABELS } from "@/lib/clientTypeLabels";
import {
  ORDER_STATUS_LABELS,
  ORDER_STATUS_COLORS,
  nextOrderStatus,
  timeSince,
  orderItemsPreview,
  orderReference,
  orderTitle,
} from "@/lib/orderUtils";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { OrderLabelsDialog } from "@/components/admin/OrderLabelsDialog";
import { PurchaseDecisionModal } from "@/components/admin/PurchaseDecisionModal";
import { ProductionDecisionModal } from "@/components/admin/ProductionDecisionModal";
import { ExpeditionConferenceList } from "@/components/admin/ExpeditionConferenceList";
import { useApp } from "@/store/useApp";
import { fetchDriverOptions, type DriverOption } from "@/lib/adminDeliveryApi";
import { FulfillmentModeToggle } from "@/components/admin/FulfillmentModeToggle";
import {
  ExpeditionConferenceFields,
  type ExpeditionConditionInfo,
  type ExpeditionConferenceValue,
} from "@/components/admin/ExpeditionConferenceFields";
import { fetchAdminCustomer } from "@/lib/adminCustomersApi";
import { fetchExpeditionPricingPreview, type ExpeditionPricingPreview } from "@/lib/ordersApi";
import { addOrderItem, cancelOrderItem, downloadConferencePdf, downloadOrderExport, downloadPurchaseListExport, fetchAdminOrderHistory, patchOrderItem } from "@/lib/ordersApi";
import { fetchCatalogSnapshot, type CatalogSnapshot } from "@/lib/catalogApi";
import { getApiErrorMessage } from "@/lib/api";
import {
  ArrowRight,
  Building2,
  Calendar,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Tag,
  CircleSlash,
  Clock,
  ChefHat,
  FileSpreadsheet,
  FileText,
  History,
  Loader2,
  MapPin,
  Package,
  PackageCheck,
  Pencil,
  Plus,
  RotateCcw,
  Save,
  Scissors,
  Search,
  StickyNote,
  FilePen,
  ShoppingCart,
  Truck,
  UserRound,
  X,
  XCircle,
} from "lucide-react";
import {
  canSeeOrderPricing,
  hasPermission,
  type FulfillmentMode,
  type OrderDetail,
  type OrderHistoryEvent,
  type OrderItem,
  type OrderProductionListItem,
  type OrderPurchaseListItem,
  type OrderStatus,
  type ProductionItemStatus,
} from "@/lib/types";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { OrderAlterationDialog } from "@/components/admin/OrderAlterationDialog";

interface Props {
  order: OrderDetail | null;
  loading: boolean;
  onOpenChange: (open: boolean) => void;
  onChangeStatus: (
    orderId: string,
    status: OrderStatus,
    transitionDate?: string,
    driverId?: string,
    fulfillmentMode?: FulfillmentMode,
    conference?: ExpeditionConferenceValue,
  ) => Promise<void>;
  onStartPurchase: (orderId: string) => Promise<void>;
  onUpdatePurchaseListItem: (
    orderId: string,
    input: {
      productId: string;
      unit: string;
      purchased: boolean;
      useStock?: boolean;
      purchasedQuantity?: number;
      reason?: string;
    },
  ) => Promise<void>;
  onUpdateProductionListItem: (
    orderId: string,
    input: {
      productId: string;
      unit: string;
      cut?: string | null;
      status: ProductionItemStatus;
      useStock?: boolean;
      producedQuantity?: number;
      reason?: string;
    },
  ) => Promise<void>;
  onUpdateExpeditionListItem?: (
    orderId: string,
    input: {
      productId: string;
      unit: string;
      cut?: string | null;
      status: "PENDENTE" | "CARREGADO" | "EM_FALTA";
      loadedQuantity?: number;
      reason?: string;
    },
  ) => Promise<void>;
  onOrderUpdated?: (updated: OrderDetail) => void;
}

function formatDateTime(value?: string) {
  if (!value) return "—";
  return new Date(value).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "America/Sao_Paulo",
  });
}

function formatDate(value?: string) {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "America/Sao_Paulo",
  });
}

function getBusinessDateInputValue(value?: string) {
  const date = value ? new Date(value) : new Date();
  const parts = new Intl.DateTimeFormat("pt-BR", {
    timeZone: "America/Sao_Paulo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const part = (type: string) => parts.find((item) => item.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")}`;
}

export function OrderDetailDialog({
  order,
  loading,
  onOpenChange,
  onChangeStatus,
  onStartPurchase,
  onUpdatePurchaseListItem,
  onUpdateProductionListItem,
  onUpdateExpeditionListItem,
  onOrderUpdated,
}: Props) {
  const colors = order ? ORDER_STATUS_COLORS[order.status] : null;
  const next = order ? nextOrderStatus(order.status) : null;
  const clientCommercialStatus = useApp((state) =>
    order ? state.clients.find((client) => client.id === order.clientId)?.status ?? null : null,
  );
  const customerValidated = clientCommercialStatus === "VALIDADO";
  const account = useApp((s) => s.account);
  const showPricing = canSeeOrderPricing(account);
  // Ações de pedido por permissão (o backend revalida cada transição/alteração).
  const canExportOrder = hasPermission(account, "PEDIDOS.PEDIDO.EXPORTAR");
  const canImportOrder = hasPermission(account, "PEDIDOS.PEDIDO.IMPORTAR");
  const canAlterOrder = hasPermission(account, "PEDIDOS.PEDIDO.ALTERAR_TOTAL");
  const canGenerateLabels = hasPermission(account, "ETIQUETAS.ETIQUETA.GERAR");
  const canCancelOrder = hasPermission(account, "PEDIDOS.PEDIDO.CANCELAR");
  const [purchaseModal, setPurchaseModal] = useState<{ item: OrderPurchaseListItem; mode: "partial" | "none" } | null>(null);
  const [productionModal, setProductionModal] = useState<{
    item: OrderProductionListItem;
    target: "PRONTO" | "PARCIAL" | "SEM_MERCADORIA";
  } | null>(null);
  const [productionCancelTarget, setProductionCancelTarget] = useState<OrderProductionListItem | null>(null);
  const [cancelItemTarget, setCancelItemTarget] = useState<OrderItem | null>(null);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [addingNewItem, setAddingNewItem] = useState(false);
  const [itemsCollapsed, setItemsCollapsed] = useState(false);
  const [labelsOpen, setLabelsOpen] = useState(false);
  const [catalog, setCatalog] = useState<CatalogSnapshot | null>(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const ensureCatalog = async () => {
    if (catalog) return catalog;
    setCatalogLoading(true);
    try {
      const snap = await fetchCatalogSnapshot();
      setCatalog(snap);
      return snap;
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Não foi possível carregar o catálogo"));
      return null;
    } finally {
      setCatalogLoading(false);
    }
  };
  const [cancellingItem, setCancellingItem] = useState(false);
  const [advanceModal, setAdvanceModal] = useState<{ toStatus: OrderStatus } | null>(null);
  const [advanceDateInput, setAdvanceDateInput] = useState<string>("");
  const [advanceFulfillmentMode, setAdvanceFulfillmentMode] = useState<FulfillmentMode>("ENTREGA");
  const [advanceDriverId, setAdvanceDriverId] = useState<string>("");
  const [advanceDrivers, setAdvanceDrivers] = useState<DriverOption[]>([]);
  const [advanceConference, setAdvanceConference] =
    useState<ExpeditionConferenceValue>({ nfNumber: "", boletoConfirmed: false, boletoNote: "" });
  const [advanceCondition, setAdvanceCondition] = useState<ExpeditionConditionInfo | null>(null);
  const [advancePricing, setAdvancePricing] = useState<ExpeditionPricingPreview | null>(null);
  const [advanceSubmitting, setAdvanceSubmitting] = useState(false);
  const [alterationOpen, setAlterationOpen] = useState(false);

  const openAdvanceModal = () => {
    if (!order || !next) return;
    setAdvanceDateInput(getBusinessDateInputValue());
    setAdvanceFulfillmentMode("ENTREGA");
    setAdvanceDriverId("");
    setAdvanceConference({ nfNumber: "", boletoConfirmed: false, boletoNote: "" });
    setAdvanceCondition(null);
    setAdvancePricing(null);
    setAdvanceModal({ toStatus: next });
    if (next === "EM_ROTA" && advanceDrivers.length === 0) {
      void fetchDriverOptions().then(setAdvanceDrivers).catch(() => setAdvanceDrivers([]));
    }
    if (next === "EM_ROTA" && order.clientId) {
      void fetchAdminCustomer(order.clientId)
        .then((c) => setAdvanceCondition({
          paymentMethod: c.defaultPaymentMethod ?? null,
          paymentTermDays: c.defaultPaymentTermDays ?? null,
          installments: c.defaultInstallments ?? null,
        }))
        .catch(() => setAdvanceCondition(null));
      void fetchExpeditionPricingPreview(order.id).then(setAdvancePricing).catch(() => setAdvancePricing(null));
    }
  };

  const submitAdvance = async () => {
    if (!order || !advanceModal) return;
    if (!advanceDateInput) {
      toast.error("Informe a data");
      return;
    }
    const isRelease = advanceModal.toStatus === "EM_ROTA";
    const pickup = isRelease && advanceFulfillmentMode === "RETIRADA";
    if (isRelease && !pickup && !advanceDriverId) {
      toast.error("Selecione o motorista responsável pela entrega");
      return;
    }
    // NF opcional na conclusão da expedição (obrigatoriedade suspensa por período indeterminado).
    if (isRelease && advancePricing && !advancePricing.allPriced) {
      toast.error("Há itens sem preço resolvível. Cadastre o preço na tabela do cliente antes de concluir.");
      return;
    }
    setAdvanceSubmitting(true);
    try {
      await onChangeStatus(
        order.id,
        advanceModal.toStatus,
        advanceDateInput,
        isRelease && !pickup ? advanceDriverId : undefined,
        isRelease ? advanceFulfillmentMode : undefined,
        isRelease ? {
          ...advanceConference,
          expectedTotal: advancePricing?.totalRaw ?? undefined,
          expectedSignature: advancePricing?.conferenceSignature ?? undefined,
        } : undefined,
      );
      toast.success(`${orderTitle(order)} → ${ORDER_STATUS_LABELS[advanceModal.toStatus]}`);
      setAdvanceModal(null);
      setAdvanceDateInput("");
      setAdvanceDriverId("");
      // Pedido avançou para o próximo status: fecha o detalhe para o usuário ver a movimentação na aba seguinte.
      onOpenChange(false);
    } catch (error) {
      // O handler do dashboard já exibe o erro real e lança este sentinel — não duplicar o toast.
      // Nos demais fluxos, mostrar a mensagem real do backend (ex.: cliente sem condição financeira),
      // em vez de uma mensagem genérica que não diz o motivo.
      if (!(error instanceof Error && error.message === "transition-blocked")) {
        toast.error(getApiErrorMessage(error, "Não foi possível atualizar o pedido"));
      }
    } finally {
      setAdvanceSubmitting(false);
    }
  };
  const startPurchase = async () => {
    if (!order) return;
    try {
      await onStartPurchase(order.id);
      toast.success("Pedido marcado como em compra");
    } catch {
      toast.error("Nao foi possivel iniciar a compra");
    }
  };
  const purchaseItemKey = (item: OrderPurchaseListItem) => `${item.productId}:${item.unit}`;
  const markTotal = async (item: OrderPurchaseListItem) => {
    if (!order) return;
    try {
      await onUpdatePurchaseListItem(order.id, {
        productId: item.productId,
        unit: item.unit,
        purchased: true,
        purchasedQuantity: item.purchaseQuantity,
      });
      toast.success("Item marcado como comprado");
    } catch {
      toast.error("Nao foi possivel atualizar o item");
    }
  };
  const markStock = async (item: OrderPurchaseListItem) => {
    if (!order) return;
    try {
      await onUpdatePurchaseListItem(order.id, {
        productId: item.productId,
        unit: item.unit,
        purchased: false,
        useStock: true,
      });
      toast.success("Item marcado como em estoque");
    } catch {
      toast.error("Nao foi possivel atualizar o item");
    }
  };
  const setProductionStatus = async (
    item: OrderProductionListItem,
    payload: {
      status: ProductionItemStatus;
      useStock?: boolean;
      producedQuantity?: number;
      reason?: string;
    },
    successMessage: string,
  ): Promise<boolean> => {
    if (!order) return false;
    try {
      await onUpdateProductionListItem(order.id, {
        productId: item.productId,
        unit: item.unit,
        cut: item.cut ?? null,
        ...payload,
      });
      toast.success(successMessage);
      return true;
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Não foi possível atualizar o item"));
      return false;
    }
  };
  const startProduction = (item: OrderProductionListItem) =>
    setProductionStatus(item, { status: "EM_ANDAMENTO" }, "Produção iniciada");
  const markInNaturaReady = (item: OrderProductionListItem) =>
    setProductionStatus(item, { status: "PRONTO" }, "Item marcado como pronto");
  const revertProductionToPending = (item: OrderProductionListItem) =>
    setProductionStatus(item, { status: "PENDENTE" }, "Item voltou para pendente");
  const cancelProduction = (item: OrderProductionListItem) =>
    setProductionStatus(item, { status: "CANCELADO" }, "Item cancelado");
  const submitProductionModal = async (input: { useStock?: boolean; quantity?: number; reason?: string }) => {
    if (!productionModal) return;
    const { item, target } = productionModal;
    const ok = await setProductionStatus(
      item,
      {
        status: target,
        useStock: target === "PRONTO" ? !!input.useStock : false,
        producedQuantity: target === "PARCIAL" ? input.quantity : undefined,
        reason: input.reason,
      },
      "Lista de produção atualizada",
    );
    if (ok) setProductionModal(null);
  };
  const submitModal = async (input: { purchasedQuantity: number; reason: string }) => {
    if (!order || !purchaseModal) return;
    const { item, mode } = purchaseModal;
    try {
      await onUpdatePurchaseListItem(order.id, {
        productId: item.productId,
        unit: item.unit,
        purchased: false,
        purchasedQuantity: mode === "partial" ? input.purchasedQuantity : 0,
        reason: input.reason,
      });
      toast.success("Lista de compra atualizada");
      setPurchaseModal(null);
    } catch {
      toast.error("Nao foi possivel atualizar o item");
    }
  };

  const handleCancelItem = async () => {
    if (!cancelItemTarget || !order) return;
    setCancellingItem(true);
    try {
      const updated = await cancelOrderItem(order.id, cancelItemTarget.id);
      onOrderUpdated?.(updated);
      toast.success(`${cancelItemTarget.productName} cancelado`);
      setCancelItemTarget(null);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Não foi possível cancelar o item"));
    } finally {
      setCancellingItem(false);
    }
  };

  const canCancelItems = order && ["CRIADO", "EM_COMPRA", "EM_PRODUCAO"].includes(order.status);

  const exportOrder = async (format: "pdf" | "xlsx") => {
    if (!order) return;
    try {
      const { filename, blob } = await downloadOrderExport(order.id, format);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Pedido exportado (${format.toUpperCase()})`);
    } catch {
      toast.error("Não foi possível exportar o pedido");
    }
  };

  const exportConferencePdf = async () => {
    if (!order) return;
    try {
      const { filename, blob } = await downloadConferencePdf(order.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF da expedição gerado");
    } catch (err) {
      const code = err instanceof Error ? err.message : "";
      if (code === "NO_CONFERENCE") {
        toast.error("Pedido sem conferência de expedição — disponível após concluir a expedição.");
      } else if (code === "FORBIDDEN_PDF") {
        toast.error("Sem permissão para o documento financeiro do pedido.");
      } else {
        toast.error("Não foi possível gerar o PDF da expedição");
      }
    }
  };

  return (
    <Dialog open={loading || !!order} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92vh] w-[calc(100vw-2rem)] max-w-5xl flex-col gap-0 overflow-hidden border-border bg-background p-0 shadow-2xl sm:rounded-xl">
        {loading && !order ? (
          <div className="flex flex-1 items-center justify-center px-6 py-10 text-sm text-muted-foreground">
            <div className="text-center">
              <Loader2 className="mx-auto mb-3 h-5 w-5 animate-spin text-primary" />
              Carregando pedido...
            </div>
          </div>
        ) : order && colors ? (
          <>
            <DialogHeader className="border-b border-border bg-surface-elevated px-6 py-5 pr-14 text-left">
              <DialogTitle className="flex items-center gap-3 font-display text-xl">
                <span className={cn("inline-flex h-2.5 w-2.5 rounded-full", colors.dot)} />
                <span className="min-w-0 truncate">{orderTitle(order)}</span>
                <span className={cn("rounded-md px-2 py-0.5 text-xs font-medium", colors.bg, colors.text)}>
                  {ORDER_STATUS_LABELS[order.status]}
                </span>
              </DialogTitle>
              <DialogDescription>
                {orderReference(order)} · {orderItemsPreview(order)}
              </DialogDescription>
              {canExportOrder && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Exportar pedido
                  </span>
                  <Button size="sm" variant="outline" onClick={() => void exportOrder("pdf")}>
                    <FileText className="h-4 w-4" /> PDF
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => void exportOrder("xlsx")}>
                    <FileSpreadsheet className="h-4 w-4" /> XLSX
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => void exportConferencePdf()}>
                    <FileText className="h-4 w-4" /> PDF da expedição
                  </Button>
                </div>
              )}
            </DialogHeader>

            <div className="flex-1 space-y-6 overflow-y-auto px-6 py-5">
              <section className="grid gap-3 sm:grid-cols-2">
                <InfoBlock icon={Building2} label="Cliente">
                  <div className="font-medium">{order.clientName}</div>
                  <div className="text-xs text-muted-foreground">{CLIENT_TYPE_LABELS[order.clientType]}</div>
                </InfoBlock>
                <InfoBlock icon={Calendar} label="Janela de entrega">
                  <div className="font-medium">{order.deliveryWindow ?? "—"}</div>
                  <div className="text-xs text-muted-foreground">Criado {timeSince(order.createdAt)}</div>
                </InfoBlock>
                <InfoBlock icon={ShoppingCart} label="Iniciado em compra">
                  <div className="font-medium">{formatDateTime(order.purchaseStartedAt)}</div>
                </InfoBlock>
                {order.deliveryUnit && (
                  <InfoBlock icon={MapPin} label="Unidade de entrega">
                    <div className="font-medium">
                      {order.deliveryUnit.name}
                      {order.deliveryUnit.externalCode && (
                        <span className="text-xs font-normal text-muted-foreground"> · {order.deliveryUnit.externalCode}</span>
                      )}
                    </div>
                    {(order.deliveryUnit.address || order.deliveryUnit.city) && (
                      <div className="text-xs text-muted-foreground">
                        {[order.deliveryUnit.address, order.deliveryUnit.district, order.deliveryUnit.city]
                          .filter(Boolean)
                          .join(", ")}
                      </div>
                    )}
                    {order.deliveryUnit.deliveryWindowText && (
                      <div className="text-xs text-muted-foreground">{order.deliveryUnit.deliveryWindowText}</div>
                    )}
                  </InfoBlock>
                )}
              </section>

              {order.status === "CRIADO" && (
                <section className="rounded-lg border border-border bg-surface-elevated p-3">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-end">
                    <Button
                      variant="hero"
                      size="sm"
                      onClick={() => void startPurchase()}
                      disabled={!customerValidated}
                      title={!customerValidated ? "Cliente em análise. Aguarde validação." : undefined}
                    >
                      <ShoppingCart className="h-4 w-4" /> Marcar como em compra
                      {!customerValidated && " (cliente em análise)"}
                    </Button>
                  </div>
                </section>
              )}

              {order.notes && (
                <div className="rounded-lg border border-warning/30 bg-warning/5 p-3 text-sm">
                  <span className="font-medium">Observações: </span>
                  {order.notes}
                </div>
              )}

              <section>
                <div className="mb-2 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CollapseToggle
                      collapsed={itemsCollapsed}
                      onToggle={() => setItemsCollapsed((v) => !v)}
                      label="itens"
                    />
                    <h3 className="font-display text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                      Itens ({order.items.length})
                    </h3>
                  </div>
                  {canCancelItems && canAlterOrder ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        await ensureCatalog();
                        setAddingNewItem(true);
                        setEditingItemId(null);
                      }}
                      disabled={addingNewItem || catalogLoading}
                    >
                      {catalogLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                      Adicionar item
                    </Button>
                  ) : null}
                </div>
                <div className={cn("surface-card divide-y divide-border", itemsCollapsed && "hidden")}>
                  {addingNewItem && catalog ? (
                    <AddItemRow
                      catalog={catalog}
                      onCancel={() => setAddingNewItem(false)}
                      onConfirm={async (input) => {
                        try {
                          const updated = await addOrderItem(order.id, input);
                          onOrderUpdated?.(updated);
                          toast.success("Item adicionado");
                          setAddingNewItem(false);
                        } catch (err) {
                          toast.error(getApiErrorMessage(err, "Não foi possível adicionar o item"));
                        }
                      }}
                    />
                  ) : null}
                  {order.items.map((item, index) =>
                    editingItemId === item.id && catalog ? (
                      <EditItemRow
                        key={item.id}
                        item={item}
                        catalog={catalog}
                        onCancel={() => setEditingItemId(null)}
                        onConfirm={async (input) => {
                          try {
                            const updated = await patchOrderItem(order.id, item.id, input);
                            onOrderUpdated?.(updated);
                            toast.success("Item atualizado");
                            setEditingItemId(null);
                          } catch (err) {
                            toast.error(getApiErrorMessage(err, "Não foi possível atualizar o item"));
                          }
                        }}
                      />
                    ) : (
                      <div key={`${item.productId}-${item.cut}-${item.unit}-${index}`} className={cn("flex items-center gap-3 p-3", item.cancelledAt && "opacity-50")}>
                        <div className="h-10 w-10 shrink-0 overflow-hidden rounded-lg bg-secondary text-xl">
                          {item.imageUrl ? (
                            <img src={item.imageUrl} alt={item.productName} className="h-full w-full object-cover" />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center">{item.emoji ?? "🌱"}</div>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={cn("font-display text-sm font-semibold uppercase tracking-wide", item.cancelledAt && "line-through")}>{item.productName}</span>
                            {item.cut && (
                              <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold uppercase text-primary">
                                {item.cut}
                              </span>
                            )}
                            {item.wastePercent && item.wastePercent > 0 ? (
                              <span className="inline-flex items-center gap-1 rounded bg-warning/10 px-1.5 py-0.5 text-[10px] text-warning">
                                <Scissors className="h-2.5 w-2.5" />
                                {item.wastePercent}% merma
                              </span>
                            ) : null}
                            {item.cancelledAt ? (
                              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-destructive/10 text-destructive border border-destructive/20 shrink-0">
                                Cancelado
                              </span>
                            ) : null}
                          </div>
                          <div className={cn("mt-0.5 text-xs text-muted-foreground", item.cancelledAt && "line-through")}>
                            {item.quantity} {item.unit}
                            {showPricing && item.pricePerUnit !== undefined ? ` · R$ ${item.pricePerUnit.toFixed(2)}/un` : ""}
                          </div>
                          {item.observation && (
                            <div className="mt-1 flex items-start gap-1 text-xs text-amber-700">
                              <StickyNote className="mt-0.5 h-3 w-3 shrink-0" />
                              <span className="whitespace-pre-wrap">{item.observation}</span>
                            </div>
                          )}
                        </div>
                        {showPricing && item.pricePerUnit !== undefined && (
                          <span className={cn("text-sm font-semibold tabular-nums", item.cancelledAt && "line-through")}>
                            R$ {(item.pricePerUnit * item.quantity).toFixed(2)}
                          </span>
                        )}
                        {!item.cancelledAt && canCancelItems && canAlterOrder ? (
                          <>
                            <button
                              type="button"
                              onClick={async () => {
                                await ensureCatalog();
                                setAddingNewItem(false);
                                setEditingItemId(item.id);
                              }}
                              className="ml-2 shrink-0 text-muted-foreground hover:text-primary transition-colors"
                              title="Editar item"
                              disabled={catalogLoading}
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setCancelItemTarget(item)}
                              className="shrink-0 text-muted-foreground hover:text-destructive transition-colors"
                              title="Cancelar item"
                            >
                              <XCircle className="h-4 w-4" />
                            </button>
                          </>
                        ) : null}
                      </div>
                    ),
                  )}
                  {showPricing && order.total !== undefined && (
                    <div className="flex items-baseline justify-between bg-secondary/40 p-3">
                      <span className="text-sm font-medium">Total</span>
                      <span className="font-display text-lg font-bold tabular-nums">R$ {order.total.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              </section>

              <PurchaseListSection
                order={order}
                purchaseItemKey={purchaseItemKey}
                onMarkTotal={markTotal}
                onMarkStock={markStock}
                onOpenModal={(item, mode) => setPurchaseModal({ item, mode })}
              />

              <ProductionListSection
                order={order}
                onStart={startProduction}
                onMarkReady={markInNaturaReady}
                onOpenModal={(item, target) => setProductionModal({ item, target })}
                onRevert={revertProductionToPending}
                onCancel={(item) => setProductionCancelTarget(item)}
              />

              <ExpeditionConferenceList
                order={order}
                onUpdate={async (input) => {
                  if (!onUpdateExpeditionListItem) return;
                  await onUpdateExpeditionListItem(order.id, input);
                }}
              />

              <DeliveryConfirmationSection order={order} />
              <HistorySection order={order} />
            </div>

            <div className="flex flex-wrap gap-2 border-t border-border bg-surface-elevated px-6 py-4">
              {canGenerateLabels && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setLabelsOpen(true)}
                  title="Gerar etiquetas de todos os itens do pedido"
                >
                  <Tag className="h-4 w-4" /> Gerar etiquetas
                </Button>
              )}
              {canCancelItems && canImportOrder && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setAlterationOpen(true)}
                  title="Importar XML de alteração de AF para atualizar itens"
                >
                  <FilePen className="h-4 w-4" /> Alterar via XML
                </Button>
              )}
              {next && (() => {
                // Permissão de avanço casa com o backend (baseada na etapa ATUAL / from).
                const advancePerm =
                  order.status === "CRIADO" || order.status === "EM_COMPRA" ? "PEDIDOS.COMPRA.AVANCAR"
                  : order.status === "EM_PRODUCAO" ? "PEDIDOS.PRODUCAO.AVANCAR"
                  : order.status === "EM_EXPEDICAO" ? "PEDIDOS.EXPEDICAO.AVANCAR"
                  : order.status === "EM_ROTA" ? "ENTREGAS.ENTREGADOR.CONFIRMAR"
                  : null;
                if (!advancePerm || !hasPermission(account, advancePerm)) return null;
                const pendingPurchase =
                  order.status === "EM_COMPRA" && next === "EM_PRODUCAO"
                    ? order.purchaseList.filter((i) => purchaseDecisionOf(i) === "pending").length
                    : 0;
                const blockedByCustomerValidation = next === "EM_COMPRA" && !customerValidated;
                const disabled = pendingPurchase > 0 || blockedByCustomerValidation;
                const title = blockedByCustomerValidation
                  ? "Cliente ainda em análise — valide o cadastro antes de enviar para compras"
                  : pendingPurchase > 0
                    ? `Decida todos os itens da lista de compra antes de avançar (${pendingPurchase} pendente${pendingPurchase === 1 ? "" : "s"})`
                    : undefined;
                return (
                  <Button
                    variant="hero"
                    size="sm"
                    onClick={openAdvanceModal}
                    className="ml-auto"
                    disabled={disabled}
                    title={title}
                  >
                    {next === "EM_PRODUCAO" && <ChefHat className="h-4 w-4" />}
                    {next === "EM_EXPEDICAO" && <PackageCheck className="h-4 w-4" />}
                    {next === "EM_ROTA" && <Truck className="h-4 w-4" />}
                    {next === "ENTREGUE" && <ArrowRight className="h-4 w-4" />}
                    Avançar para {ORDER_STATUS_LABELS[next]}
                    {pendingPurchase > 0 && ` (${pendingPurchase} pendente${pendingPurchase === 1 ? "" : "s"})`}
                    {blockedByCustomerValidation && " (cliente em análise)"}
                  </Button>
                );
              })()}
              {order.status !== "CANCELADO" && order.status !== "ENTREGUE" && !next && canCancelOrder && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive"
                  onClick={async () => {
                    try {
                      await onChangeStatus(order.id, "CANCELADO");
                      toast.error(`${orderTitle(order)} cancelado`);
                    } catch {
                      toast.error("Nao foi possivel cancelar o pedido");
                    }
                  }}
                >
                  <XCircle className="h-4 w-4" /> Cancelar
                </Button>
              )}
            </div>
          </>
        ) : null}
      </DialogContent>
      {purchaseModal && (
        <PurchaseDecisionModal
          item={purchaseModal.item}
          mode={purchaseModal.mode}
          onCancel={() => setPurchaseModal(null)}
          onSubmit={submitModal}
        />
      )}
      {productionModal && (
        <ProductionDecisionModal
          item={productionModal.item}
          target={productionModal.target}
          onCancel={() => setProductionModal(null)}
          onSubmit={submitProductionModal}
        />
      )}
      <AlertDialog
        open={productionCancelTarget !== null}
        onOpenChange={(open) => !open && setProductionCancelTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar item da produção</AlertDialogTitle>
            <AlertDialogDescription>
              {productionCancelTarget && (
                <>
                  <strong>{productionCancelTarget.productName}</strong> ficará marcado como cancelado na lista de produção.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (productionCancelTarget) {
                  void cancelProduction(productionCancelTarget);
                }
                setProductionCancelTarget(null);
              }}
            >
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Dialog
        open={advanceModal !== null}
        onOpenChange={(open) => {
          if (!open && !advanceSubmitting) {
            setAdvanceModal(null);
            setAdvanceDateInput("");
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {advanceModal ? `Mover para ${ORDER_STATUS_LABELS[advanceModal.toStatus]}` : ""}
            </DialogTitle>
            <DialogDescription>
              Informe a data desta etapa.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <label className="text-sm font-medium">Data</label>
              <DatePicker value={advanceDateInput} onChange={setAdvanceDateInput} />
            </div>
            {advanceModal?.toStatus === "EM_ROTA" && (
              <>
                <FulfillmentModeToggle value={advanceFulfillmentMode} onChange={setAdvanceFulfillmentMode} />
                {advanceFulfillmentMode === "ENTREGA" ? (
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Motorista responsável *</label>
                    <Select value={advanceDriverId || undefined} onValueChange={setAdvanceDriverId}>
                      <SelectTrigger>
                        <SelectValue placeholder={advanceDrivers.length === 0 ? "Carregando..." : "Selecione o motorista"} />
                      </SelectTrigger>
                      <SelectContent>
                        {advanceDrivers.map((d) => (
                          <SelectItem key={d.id} value={d.id}>
                            {d.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <p className="rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-700">
                    O cliente vai retirar na fábrica. O pedido fica em <strong>Em entrega</strong> sem motorista e você
                    confirma a entrega manualmente quando ele buscar.
                  </p>
                )}
                <ExpeditionConferenceFields
                  value={advanceConference}
                  onChange={setAdvanceConference}
                  condition={advanceCondition}
                  pricing={advancePricing}
                />
              </>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setAdvanceModal(null);
                setAdvanceDateInput("");
              }}
              disabled={advanceSubmitting}
            >
              Cancelar
            </Button>
            <Button onClick={() => void submitAdvance()} disabled={advanceSubmitting}>
              {advanceSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AlertDialog open={cancelItemTarget !== null} onOpenChange={(open) => !open && setCancelItemTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar item?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{cancelItemTarget?.productName}</strong>
              {" "}— {cancelItemTarget?.quantity} {cancelItemTarget?.unit} será removido deste pedido.
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancellingItem}>Voltar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => void handleCancelItem()}
              disabled={cancellingItem}
            >
              {cancellingItem ? "Cancelando..." : "Cancelar item"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <OrderAlterationDialog
        orderId={order?.id ?? null}
        open={alterationOpen}
        onOpenChange={setAlterationOpen}
        onApplied={(updated) => onOrderUpdated?.(updated)}
      />
      <OrderLabelsDialog
        open={labelsOpen}
        orderId={order?.id ?? null}
        onClose={() => setLabelsOpen(false)}
      />
    </Dialog>
  );
}

type PurchaseDecision = "total" | "partial" | "none" | "stock" | "pending";

function purchaseDecisionOf(item: OrderPurchaseListItem): PurchaseDecision {
  if (item.purchased) return "total";
  if (item.useStock) return "stock";
  if (item.purchasedQuantity !== undefined && item.purchasedQuantity > 0) return "partial";
  if (item.reason || item.purchaseUpdatedBy) return "none";
  return "pending";
}

function CollapseToggle({
  collapsed,
  onToggle,
  label,
}: {
  collapsed: boolean;
  onToggle: () => void;
  label: string;
}) {
  return (
    <Button
      type="button"
      size="icon"
      variant="ghost"
      className="h-7 w-7 shrink-0 text-muted-foreground"
      onClick={onToggle}
      aria-expanded={!collapsed}
      aria-label={collapsed ? `Expandir ${label}` : `Recolher ${label}`}
      title={collapsed ? "Expandir" : "Recolher"}
    >
      {collapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
    </Button>
  );
}

function PurchaseListSection({
  order,
  purchaseItemKey,
  onMarkTotal,
  onMarkStock,
  onOpenModal,
}: {
  order: OrderDetail;
  purchaseItemKey: (item: OrderPurchaseListItem) => string;
  onMarkTotal: (item: OrderPurchaseListItem) => Promise<void>;
  onMarkStock: (item: OrderPurchaseListItem) => Promise<void>;
  onOpenModal: (item: OrderPurchaseListItem, mode: "partial" | "none") => void;
}) {
  const editable = order.status === "EM_COMPRA";
  const list = order.purchaseList;
  const total = list.length;
  const decided = list.filter((i) => purchaseDecisionOf(i) !== "pending").length;
  const pending = total - decided;

  const [collapsed, setCollapsed] = useState(false);

  const exportFile = async (format: "pdf" | "xlsx") => {
    try {
      const { filename, blob } = await downloadPurchaseListExport(order.id, format);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Lista de compra exportada (${format.toUpperCase()})`);
    } catch {
      toast.error("Nao foi possivel exportar");
    }
  };

  return (
    <section>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <CollapseToggle
            collapsed={collapsed}
            onToggle={() => setCollapsed((v) => !v)}
            label="lista de compra"
          />
          <h3 className="flex items-center gap-2 font-display text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            <ShoppingCart className="h-4 w-4" /> Lista de compra
          </h3>
          {total > 0 && (
            <span className="text-xs text-muted-foreground">
              {decided}/{total} decididos{pending > 0 ? ` · ${pending} pendentes` : ""}
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => void exportFile("pdf")}>
            <FileText className="h-4 w-4" /> PDF
          </Button>
          <Button size="sm" variant="outline" onClick={() => void exportFile("xlsx")}>
            <FileSpreadsheet className="h-4 w-4" /> XLSX
          </Button>
        </div>
      </div>

      {!editable && (
        <div className="mb-2 rounded-md border border-border bg-secondary/40 px-3 py-2 text-xs text-muted-foreground">
          As marcações ficam disponíveis quando o pedido estiver <strong>Em compra</strong>.
        </div>
      )}

      <div className={cn("surface-card divide-y divide-border", collapsed && "hidden")}>
        {list.length === 0 ? (
          <div className="p-4 text-sm text-muted-foreground">Nenhum item para compra.</div>
        ) : (
          list.map((item) => {
            const key = purchaseItemKey(item);
            const decision = purchaseDecisionOf(item);
            return (
              <div key={key} className="flex flex-wrap items-center gap-3 p-3">
                <div className="h-10 w-10 shrink-0 overflow-hidden rounded-lg bg-secondary text-xl">
                  {item.imageUrl ? (
                    <img src={item.imageUrl} alt={item.productName} className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center">{item.emoji ?? "🌱"}</div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-display text-sm font-semibold uppercase tracking-wide">{item.productName}</span>
                    <DecisionBadge decision={decision} />
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    <span className="tabular-nums">{item.purchaseQuantity}</span> {item.unit}
                    {decision === "partial" && item.purchasedQuantity !== undefined && (
                      <> · comprado <span className="tabular-nums text-foreground">{item.purchasedQuantity}</span> {item.unit}</>
                    )}
                    {item.reason ? <> · {item.reason}</> : null}
                  </div>
                </div>
                {editable && (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant={decision === "total" ? "hero" : "outline"}
                      onClick={() => void onMarkTotal(item)}
                    >
                      <CheckCircle2 className="h-4 w-4" /> Total
                    </Button>
                    <Button
                      size="sm"
                      variant={decision === "partial" ? "hero" : "outline"}
                      onClick={() => onOpenModal(item, "partial")}
                    >
                      Parcial
                    </Button>
                    <Button
                      size="sm"
                      variant={decision === "stock" ? "hero" : "outline"}
                      onClick={() => void onMarkStock(item)}
                    >
                      <Package className="h-4 w-4" /> Em estoque
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className={decision === "none" ? "border-destructive text-destructive" : "text-destructive"}
                      onClick={() => onOpenModal(item, "none")}
                    >
                      <CircleSlash className="h-4 w-4" /> Não comprado
                    </Button>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </section>
  );
}

function DecisionBadge({ decision }: { decision: PurchaseDecision }) {
  if (decision === "total") {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-success/10 px-1.5 py-0.5 text-[10px] text-success">
        <CheckCircle2 className="h-3 w-3" /> Comprado
      </span>
    );
  }
  if (decision === "partial") {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-warning/10 px-1.5 py-0.5 text-[10px] text-warning">
        Parcial
      </span>
    );
  }
  if (decision === "none") {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-destructive/10 px-1.5 py-0.5 text-[10px] text-destructive">
        <CircleSlash className="h-3 w-3" /> Não comprado
      </span>
    );
  }
  if (decision === "stock") {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-info/10 px-1.5 py-0.5 text-[10px] text-info">
        <Package className="h-3 w-3" /> Em estoque
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded bg-secondary px-1.5 py-0.5 text-[10px] text-muted-foreground">
      Pendente
    </span>
  );
}

// PurchaseDecisionModal extracted to its own file for reuse by BuyerOrderDialog.

const PRODUCTION_STATUS_LABEL: Record<ProductionItemStatus, string> = {
  PENDENTE: "Pendente",
  EM_ANDAMENTO: "Em andamento",
  PRONTO: "Pronto",
  PARCIAL: "Parcial",
  SEM_MERCADORIA: "Sem mercadoria",
  CANCELADO: "Cancelado",
};

function ProductionListSection({
  order,
  onStart,
  onMarkReady,
  onOpenModal,
  onRevert,
  onCancel,
}: {
  order: OrderDetail;
  onStart: (item: OrderProductionListItem) => Promise<unknown>;
  onMarkReady: (item: OrderProductionListItem) => Promise<unknown>;
  onOpenModal: (item: OrderProductionListItem, target: "PRONTO" | "PARCIAL" | "SEM_MERCADORIA") => void;
  onRevert: (item: OrderProductionListItem) => Promise<unknown>;
  onCancel: (item: OrderProductionListItem) => void;
}) {
  const [collapsed, setCollapsed] = useState(false);
  if (order.status === "CRIADO" || order.status === "EM_COMPRA") return null;
  const editable = order.status === "EM_PRODUCAO";
  const list = order.productionList;
  const total = list.length;
  const decided = list.filter((i) => i.status !== "PENDENTE" && i.status !== "EM_ANDAMENTO").length;
  const pending = total - decided;

  return (
    <section>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <CollapseToggle
            collapsed={collapsed}
            onToggle={() => setCollapsed((v) => !v)}
            label="lista de produção"
          />
          <h3 className="flex items-center gap-2 font-display text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            <ChefHat className="h-4 w-4" /> Lista de produção
          </h3>
          {total > 0 && (
            <span className="text-xs text-muted-foreground">
              {decided}/{total} decididos{pending > 0 ? ` · ${pending} pendentes` : ""}
            </span>
          )}
        </div>
      </div>

      {!editable && (
        <div className="mb-2 rounded-md border border-border bg-secondary/40 px-3 py-2 text-xs text-muted-foreground">
          As marcações ficam disponíveis quando o pedido estiver <strong>Em produção</strong>.
        </div>
      )}

      <div className={cn("surface-card divide-y divide-border", collapsed && "hidden")}>
        {list.length === 0 ? (
          <div className="p-4 text-sm text-muted-foreground">Nenhum item para produção.</div>
        ) : (
          list.map((item) => {
            const isCancelled = item.status === "CANCELADO";
            return (
              <div
                key={`${item.productId}:${item.unit}:${item.cut ?? ""}`}
                className={cn(
                  "flex flex-wrap items-center gap-3 p-3",
                  isCancelled && "opacity-60",
                )}
              >
                <div className="h-10 w-10 shrink-0 overflow-hidden rounded-lg bg-secondary text-xl">
                  {item.imageUrl ? (
                    <img src={item.imageUrl} alt={item.productName} className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center">{item.emoji ?? "🌱"}</div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-display text-sm font-semibold uppercase tracking-wide">{item.productName}</span>
                    {item.cut && (
                      <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold uppercase text-primary">
                        {item.cut}
                      </span>
                    )}
                    <ProductionBadge status={item.status} />
                    {!item.isProcessed && (
                      <span className="inline-flex items-center gap-1 rounded bg-secondary px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                        in natura
                      </span>
                    )}
                    {item.useStock && item.status === "PRONTO" && (
                      <span className="inline-flex items-center gap-1 rounded bg-info/10 px-1.5 py-0.5 text-[10px] text-info">
                        <Package className="h-3 w-3" /> do estoque
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    solicitado <span className="tabular-nums text-foreground">{item.requestedQuantity}</span> {item.unit}
                    {" · "}comprado <span className="tabular-nums text-foreground">{item.purchasedQuantity}</span> {item.unit}
                    {item.status === "PARCIAL" && item.producedQuantity !== undefined && (
                      <> · produzido <span className="tabular-nums text-foreground">{item.producedQuantity}</span> {item.unit}</>
                    )}
                    {item.reason ? <> · {item.reason}</> : null}
                  </div>
                </div>
                {editable && !isCancelled && (
                  <div className="flex flex-wrap gap-2">
                    {item.isProcessed ? (
                      <>
                        {item.status === "PENDENTE" && (
                          <Button size="sm" variant="outline" onClick={() => void onStart(item)}>
                            Iniciar
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant={item.status === "PRONTO" ? "hero" : "outline"}
                          onClick={() => onOpenModal(item, "PRONTO")}
                        >
                          <CheckCircle2 className="h-4 w-4" /> Pronto
                        </Button>
                        <Button
                          size="sm"
                          variant={item.status === "PARCIAL" ? "hero" : "outline"}
                          onClick={() => onOpenModal(item, "PARCIAL")}
                        >
                          Parcial
                        </Button>
                        <Button
                          size="sm"
                          variant={item.status === "SEM_MERCADORIA" ? "hero" : "outline"}
                          onClick={() => onOpenModal(item, "SEM_MERCADORIA")}
                        >
                          <CircleSlash className="h-4 w-4" /> Sem mercadoria
                        </Button>
                        {(item.status === "PRONTO" || item.status === "PARCIAL" || item.status === "SEM_MERCADORIA") && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-muted-foreground"
                            title="Desfazer: voltar o item para pendente"
                            onClick={() => void onRevert(item)}
                          >
                            <RotateCcw className="h-4 w-4" /> Voltar p/ pendente
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-destructive"
                          onClick={() => onCancel(item)}
                        >
                          <XCircle className="h-4 w-4" /> Cancelar
                        </Button>
                      </>
                    ) : (
                      <>
                        {item.status !== "PRONTO" && (
                          <Button size="sm" variant="outline" onClick={() => void onMarkReady(item)}>
                            <CheckCircle2 className="h-4 w-4" /> Pronto
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant={item.status === "SEM_MERCADORIA" ? "hero" : "outline"}
                          onClick={() => onOpenModal(item, "SEM_MERCADORIA")}
                        >
                          <CircleSlash className="h-4 w-4" /> Sem mercadoria
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-destructive"
                          onClick={() => onCancel(item)}
                        >
                          <XCircle className="h-4 w-4" /> Cancelar
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </section>
  );
}

function ProductionBadge({ status }: { status: ProductionItemStatus }) {
  if (status === "PRONTO") {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-success/10 px-1.5 py-0.5 text-[10px] text-success">
        <CheckCircle2 className="h-3 w-3" /> {PRODUCTION_STATUS_LABEL.PRONTO}
      </span>
    );
  }
  if (status === "EM_ANDAMENTO") {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-info/10 px-1.5 py-0.5 text-[10px] text-info">
        {PRODUCTION_STATUS_LABEL.EM_ANDAMENTO}
      </span>
    );
  }
  if (status === "PARCIAL") {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-warning/10 px-1.5 py-0.5 text-[10px] text-warning">
        {PRODUCTION_STATUS_LABEL.PARCIAL}
      </span>
    );
  }
  if (status === "SEM_MERCADORIA") {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-destructive/10 px-1.5 py-0.5 text-[10px] text-destructive">
        <CircleSlash className="h-3 w-3" /> {PRODUCTION_STATUS_LABEL.SEM_MERCADORIA}
      </span>
    );
  }
  if (status === "CANCELADO") {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
        <XCircle className="h-3 w-3" /> {PRODUCTION_STATUS_LABEL.CANCELADO}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded bg-secondary px-1.5 py-0.5 text-[10px] text-muted-foreground">
      {PRODUCTION_STATUS_LABEL.PENDENTE}
    </span>
  );
}

// ProductionDecisionModal extracted to its own file for reuse by ProductionOrderDialog.

function HistorySection({ order }: { order: OrderDetail }) {
  const [fullHistory, setFullHistory] = useState<OrderHistoryEvent[] | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setFullHistory(null);
  }, [order.id]);

  const displayed = fullHistory ?? order.history;
  const canExpand = !fullHistory && order.historyTotal > displayed.length;
  const canCollapse = !!fullHistory && fullHistory.length > order.history.length;

  const expand = async () => {
    setLoading(true);
    try {
      const events = await fetchAdminOrderHistory(order.id);
      setFullHistory(events);
    } catch {
      toast.error("Nao foi possivel carregar o histórico");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section>
      <h3 className="mb-2 flex items-center gap-2 font-display text-sm font-semibold uppercase tracking-wider text-muted-foreground">
        <History className="h-4 w-4" /> Histórico
      </h3>
      <ol className="surface-card space-y-2 p-3">
        {displayed.map((event, index) => {
          const eventColors = event.status ? ORDER_STATUS_COLORS[event.status] : null;
          return (
            <li key={`${event.type}-${event.at}-${index}`} className="rounded-lg border border-border bg-background p-3 text-sm">
              <div className="flex items-start gap-3">
                <span className={cn("mt-1 h-2 w-2 shrink-0 rounded-full", eventColors?.dot ?? "bg-primary/60")} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={cn("font-medium", eventColors?.text ?? "text-foreground")}>{event.title}</span>
                    {event.status ? (
                      <span className={cn("rounded-md px-2 py-0.5 text-[10px] font-medium", eventColors?.bg, eventColors?.text)}>
                        {ORDER_STATUS_LABELS[event.status]}
                      </span>
                    ) : null}
                  </div>
                  {event.details ? <div className="mt-1 text-xs text-muted-foreground">{event.details}</div> : null}
                  <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                      <UserRound className="h-3 w-3" />
                      {event.actorEmail ?? "ator não registrado"}
                      {event.actorRole ? ` · ${event.actorRole}` : ""}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {new Date(event.at).toLocaleString("pt-BR")}
                    </span>
                  </div>
                </div>
              </div>
            </li>
          );
        })}
        {canExpand && (
          <li>
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              disabled={loading}
              onClick={() => void expand()}
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
              Ver histórico completo ({order.historyTotal})
            </Button>
          </li>
        )}
        {canCollapse && (
          <li>
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => setFullHistory(null)}
            >
              <ChevronUp className="h-4 w-4" />
              Recolher histórico
            </Button>
          </li>
        )}
      </ol>
    </section>
  );
}

function InfoBlock({
  icon: Icon,
  label,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="surface-card p-3">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <div className="text-sm">{children}</div>
    </div>
  );
}

function DeliveryConfirmationSection({ order }: { order: OrderDetail }) {
  const confirmation = order.deliveryConfirmation;
  if (!confirmation) return null;

  const fields: Array<[string, React.ReactNode]> = [
    ["NF", confirmation.nfNumber],
    ["Motorista", confirmation.driverName],
    ["Data/hora", formatLocaleDateTime(confirmation.deliveredAt)],
    ["Temperatura", `${confirmation.truckTemperature} °C`],
    ["Conferente", confirmation.receiverName],
    ["Documento", confirmation.receiverDocument],
  ];

  return (
    <section className="space-y-2">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">Confirmação de entrega</h3>
      <div className="surface-card grid gap-2 p-3 text-sm sm:grid-cols-2">
        {fields.map(([label, value]) => (
          <div key={label}>
            <span className="text-muted-foreground">{label}:</span>{" "}
            <span className="font-medium">{value}</span>
          </div>
        ))}
        <div className="sm:col-span-2 grid gap-3 sm:grid-cols-2 pt-2">
          <ProofThumb url={confirmation.signatureUrl} label="Assinatura" alt="assinatura" white />
          <ProofThumb url={confirmation.receiptPhotoUrl} label="Canhoto" alt="canhoto" />
        </div>
      </div>
    </section>
  );
}

function ProofThumb({ url, label, alt, white }: { url: string; label: string; alt: string; white?: boolean }) {
  return (
    <div>
      <div className="mb-1 text-xs text-muted-foreground">{label}</div>
      <a href={url} target="_blank" rel="noreferrer">
        <img
          src={url}
          alt={alt}
          className={cn("max-h-32 rounded border", white && "bg-white")}
        />
      </a>
    </div>
  );
}

function formatLocaleDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString("pt-BR");
  } catch {
    return iso;
  }
}

function EditItemRow({
  item,
  catalog,
  onCancel,
  onConfirm,
}: {
  item: OrderItem;
  catalog: CatalogSnapshot;
  onCancel: () => void;
  onConfirm: (input: { quantity?: number; catalogUnitId?: string; cut?: string | null; observation?: string | null }) => Promise<void>;
}) {
  const product = catalog.products.find((p) => p.id === item.productId);
  const availableUnitNames = (product?.availableUnits as string[] | undefined) ?? [item.unit];
  const unitOptions = catalog.units.filter((u) => availableUnitNames.includes(u.name));
  const currentUnit = unitOptions.find((u) => u.name === item.unit) ?? unitOptions[0];
  const productCuts = (product?.availableCuts as string[] | undefined) ?? [];
  const cutOptions = catalog.cutTypes.filter((c) => c.active && productCuts.includes(c.name));

  const [quantity, setQuantity] = useState<number>(item.quantity);
  const [unitId, setUnitId] = useState<string>(currentUnit?.id ?? "");
  const [cut, setCut] = useState<string>(item.cut ?? "");
  const [observation, setObservation] = useState<string>(item.observation ?? "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const patch: { quantity?: number; catalogUnitId?: string; cut?: string | null; observation?: string | null } = {};
    if (quantity !== item.quantity && Number.isFinite(quantity) && quantity > 0) patch.quantity = quantity;
    if (unitId && unitId !== currentUnit?.id) patch.catalogUnitId = unitId;
    const cutTrim = cut.trim();
    if ((item.cut ?? "") !== cutTrim) {
      patch.cut = cutTrim.length === 0 ? null : cutTrim;
    }
    const obsTrim = observation.trim();
    if ((item.observation ?? "") !== obsTrim) {
      patch.observation = obsTrim.length === 0 ? null : obsTrim;
    }
    if (Object.keys(patch).length === 0) {
      toast.info("Nada para salvar");
      return;
    }
    setSaving(true);
    try {
      await onConfirm(patch);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-3 bg-primary/5 p-3 ring-1 ring-primary/30">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 shrink-0 overflow-hidden rounded-lg bg-secondary text-xl">
          {item.imageUrl ? (
            <img src={item.imageUrl} alt={item.productName} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center">{item.emoji ?? "🌱"}</div>
          )}
        </div>
        <div className="font-display text-sm font-semibold uppercase tracking-wide flex-1">{item.productName}</div>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Quantidade</label>
          <div className="flex items-center gap-1">
            <button type="button" onClick={() => setQuantity((q) => Math.max(0.01, +(q - 1).toFixed(2)))} className="h-9 w-9 rounded border">−</button>
            <Input type="number" min={0} step="0.01" value={quantity} onChange={(e) => setQuantity(Number(e.target.value))} className="h-9 text-center" />
            <button type="button" onClick={() => setQuantity((q) => +(q + 1).toFixed(2))} className="h-9 w-9 rounded border">+</button>
          </div>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Unidade</label>
          <Select value={unitId} onValueChange={setUnitId}>
            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              {unitOptions.map((u) => (<SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Tipo de corte</label>
          <Select value={cut || "__none__"} onValueChange={(v) => setCut(v === "__none__" ? "" : v)}>
            <SelectTrigger className="h-9"><SelectValue placeholder="Sem corte" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Sem corte</SelectItem>
              {cutOptions.map((c) => (<SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <label className="mb-1 block text-xs font-medium text-muted-foreground">Observação</label>
        <Textarea
          value={observation}
          onChange={(e) => setObservation(e.target.value)}
          placeholder="Observação do item (opcional)"
          rows={2}
          className="text-sm"
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="outline" onClick={onCancel} disabled={saving}>
          <X className="h-4 w-4" /> Cancelar
        </Button>
        <Button size="sm" onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Salvar alterações
        </Button>
      </div>
    </div>
  );
}

function AddItemRow({
  catalog,
  onCancel,
  onConfirm,
}: {
  catalog: CatalogSnapshot;
  onCancel: () => void;
  onConfirm: (input: { catalogProductId: string; catalogUnitId?: string; quantity: number; cut?: string | null; observation?: string | null }) => Promise<void>;
}) {
  const [search, setSearch] = useState("");
  const [productId, setProductId] = useState<string | null>(null);
  const [unitId, setUnitId] = useState<string>("");
  const [cut, setCut] = useState<string>("");
  const [quantity, setQuantity] = useState<number>(1);
  const [observation, setObservation] = useState<string>("");
  const [saving, setSaving] = useState(false);

  const product = catalog.products.find((p) => p.id === productId) ?? null;
  const availableUnitNames = (product?.availableUnits as string[] | undefined) ?? (product ? [product.defaultUnit] : []);
  const unitOptions = catalog.units.filter((u) => availableUnitNames.includes(u.name));
  const productCuts = (product?.availableCuts as string[] | undefined) ?? [];
  const cutOptions = catalog.cutTypes.filter((c) => c.active && productCuts.includes(c.name));

  useEffect(() => {
    if (product && unitOptions.length > 0) {
      const def = unitOptions.find((u) => u.name === product.defaultUnit) ?? unitOptions[0];
      setUnitId(def.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const filtered = (() => {
    const q = search.trim().toLowerCase();
    const products = catalog.products.filter((p) => p.active);
    if (!q) return products.slice(0, 8);
    return products.filter((p) => p.name.toLowerCase().includes(q)).slice(0, 8);
  })();

  const handleConfirm = async () => {
    if (!product || !(quantity > 0)) return;
    setSaving(true);
    try {
      const cutTrim = cut.trim();
      await onConfirm({
        catalogProductId: product.id,
        catalogUnitId: unitId || undefined,
        quantity,
        cut: cutTrim.length === 0 ? null : cutTrim,
        observation: observation.trim() || null,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-3 bg-primary/5 p-3 ring-1 ring-primary/30">
      {!product ? (
        <div>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar produto..."
                className="h-9 pl-9"
              />
            </div>
            <Button size="sm" variant="outline" onClick={onCancel}>
              <X className="h-4 w-4" /> Cancelar
            </Button>
          </div>
          <div className="mt-2 max-h-56 divide-y divide-border overflow-auto rounded border bg-background">
            {filtered.length === 0 ? (
              <p className="p-3 text-center text-sm text-muted-foreground">Nenhum produto encontrado.</p>
            ) : (
              filtered.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setProductId(p.id)}
                  className="flex w-full items-center gap-3 p-2.5 text-left hover:bg-secondary/50"
                >
                  <span className="text-2xl">{p.emoji ?? "📦"}</span>
                  <span className="flex-1 text-sm font-medium uppercase tracking-wide">{p.name}</span>
                  <span className="text-xs text-muted-foreground">{p.defaultUnit}</span>
                </button>
              ))
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-3">
            <span className="text-2xl">{product.emoji ?? "📦"}</span>
            <div className="flex-1 font-display text-sm font-semibold uppercase tracking-wide">{product.name}</div>
            <Button size="sm" variant="ghost" onClick={() => { setProductId(null); setCut(""); setQuantity(1); }}>
              <Pencil className="h-3 w-3" /> trocar
            </Button>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Quantidade</label>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => setQuantity((q) => Math.max(0.01, +(q - 1).toFixed(2)))} className="h-9 w-9 rounded border">−</button>
                <Input type="number" min={0} step="0.01" value={quantity} onChange={(e) => setQuantity(Number(e.target.value))} className="h-9 text-center" />
                <button type="button" onClick={() => setQuantity((q) => +(q + 1).toFixed(2))} className="h-9 w-9 rounded border">+</button>
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Unidade</label>
              <Select value={unitId} onValueChange={setUnitId}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {unitOptions.map((u) => (<SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Tipo de corte</label>
              <Select value={cut || "__none__"} onValueChange={(v) => setCut(v === "__none__" ? "" : v)}>
                <SelectTrigger className="h-9"><SelectValue placeholder="Sem corte" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Sem corte</SelectItem>
                  {cutOptions.map((c) => (<SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Observação</label>
            <Textarea
              value={observation}
              onChange={(e) => setObservation(e.target.value)}
              placeholder="Observação do item (opcional)"
              rows={2}
              className="text-sm"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={onCancel} disabled={saving}>
              <X className="h-4 w-4" /> Cancelar
            </Button>
            <Button size="sm" onClick={handleConfirm} disabled={saving || !(quantity > 0)}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Adicionar ao pedido
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
